import React, {ChangeEventHandler, forwardRef, InputHTMLAttributes, ReactNode} from 'react'

import cn from 'classnames/bind'

import {useToggleState} from 'src/utils/hooks/useToggleState'

import s from './Input.module.scss'
import ErrorMessage from '../ErrorMessage'
import FieldLabel from '../FieldLabel'

const cx = cn.bind(s)

export interface IFieldBase {
	label?: string | ReactNode
	error?: string
	isError?: boolean
	required?: boolean | undefined
}

export interface IInputProps
	extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>,
		IFieldBase {
	variant?: 'default' | 'editable' | 'brainable' | 'new'
	size?: 'medium' | 'large'
	className?: string
	wrapperClassName?: string
	placeholder?: string
	onChange?: ChangeEventHandler<HTMLInputElement> | undefined
}

const Input = forwardRef<HTMLInputElement, IInputProps>(
	(
		{
			label,
			variant = 'default',
			size = 'medium',
			error,
			isError,
			wrapperClassName,
			className,
			...props
		},
		forwardedRef,
	) => {
		const [shouldShowPassword, toggleShowPassword] = useToggleState(false)

		return (
			<div
				className={cx('wrapper', 'global-input-wrapper', wrapperClassName, variant, size, {
					isError: isError || error,
				})}>
				<div className={cx('inner')}>
					{props.type === 'password' && (
						<button
							type="button"
							tabIndex={-1}
							className={cx('btn-toggle')}
							onClick={toggleShowPassword}
							data-testid="btn-password-toggle"
						/>
					)}
					{label ? (
						<FieldLabel variant={variant} label={label} required={props.required}>
							<input
								className={cx('input', className)}
								data-show-password={shouldShowPassword}
								data-is-password={props.type === 'password'}
								{...props}
								type={shouldShowPassword ? 'text' : props.type}
								ref={forwardedRef}
							/>
						</FieldLabel>
					) : (
						<input
							className={cx('input', className)}
							data-show-password={shouldShowPassword}
							data-is-password={props.type === 'password'}
							{...props}
							type={shouldShowPassword ? 'text' : props.type}
							ref={forwardedRef}
						/>
					)}
				</div>
				<ErrorMessage error={error} />
			</div>
		)
	},
)

Input.displayName = 'Input'

export default Input
